import notificationPreferences from './NotificationPreferences/text.js';
import signalInterval from './SignalIntervals/text.js';

export default {
  header: {
    notes: 'Alarms page title',
    message: 'Alarms',
  },
  limitReached: {
    notes: 'Information that more alarms exist than is displayed',
    message:
      'Viewing latest {{displayedAlarmCount}} out of {{totalAlarmCount}}',
  },
  tabs: {
    dashboard: {
      notes: 'Label for dashboard tab in alarms',
      message: 'Dashboard',
    },
    log: {
      notes: 'Label for alarm log tab in alarms',
      message: 'Alarm log',
    },
    signalIntervals: {
      notes: 'Label for signal intervals tab in alarm',
      message: 'Thresholds',
    },
    notifications: {
      notes: 'Label for notification preferences tab in alarm',
      message: 'Notifications',
    },
  },
  active: {
    header: {
      notes: 'Header of active alarms section',
      message: 'Active alarms',
    },
    empty: {
      text: {
        notes: 'Text in empty state when no active alarms exists',
        message: 'No active alarms, all good!',
      },
    },
    help: {
      notes: 'Help text in active alarms section',
      message: 'Active alarms and related guidance when available.',
    },
    pagination: {
      notes: 'Information that more active alarms exist than is displayed',
      message: 'Viewing {{displayedAlarmCount}} out of {{totalAlarmCount}}',
    },
  },
  latest: {
    empty: {
      text: {
        notes:
          'Text in empty state when no alarms exists in latest alarms section',
        message: 'No alarms, all good!',
      },
    },
  },
  insights: {
    header: {
      notes: 'Header of alarm insights section',
      message: 'Alarm insights',
    },
    empty: {
      text: {
        notes:
          'Texts in empty state when no alarms exists in alarm insights section',
        message:
          'No alarms for selected time range. Change the time range to explore alarms.',
      },
    },
    help: {
      notes: 'Help text in alarm insights section',
      message:
        'Alarms by type and level for the time range selected. Click on rows to reveal more data.',
    },
    groupByType: {
      header: {
        notes: 'Header of group by type section for alarm insights',
        message: 'Top 5 by type',
      },
    },
    groupBySeverity: {
      header: {
        notes: 'Header of group by severity section for alarm insights',
        message: 'Distribution by level',
      },
    },
  },
  timeline: {
    header: {
      notes: 'Header of alarm timeline section',
      message: 'Alarm timeline',
    },
    help: {
      notes: 'Help text in alarm timeline section',
      message: 'Alarms and operational behaviour for the time range selected.',
    },
    utilization: {
      idle: {
        notes: 'Label when equipment is running but is idle',
        message: 'Idle',
      },
      loaded: {
        notes: 'Label when equipment is running with load',
        message: 'Feed cycle',
      },
      noDataHelp: {
        notes:
          'Help message showing information that not utilization data is displayed as the selected date range is too large.',
        message:
          'Utilization data can not be displayed for the selected time range. Select a smaller one to show it.',
      },
    },
  },
  log: {
    empty: {
      text: {
        notes: 'Text in empty state when no alarms exists',
        message: 'No alarms, all good!',
      },
    },
  },
  details: {
    empty: {
      text: {
        notes: 'Text in empty state when no alarm is found',
        message: 'Oh-oh, unable to find the alarm.',
      },
    },
    viewSignalTrend: {
      notes: 'Button label to navigate to the signal trend for the alarm',
      message: 'View signal trend',
    },
    thresholdLowerTooltip: {
      notes: 'Tooltip for the lower threshold icon next to the breach value',
      message: 'Lower threshold',
    },
    thresholdUpperTooltip: {
      notes: 'Tooltip for the upper threshold icon next to the breach value',
      message: 'Upper threshold',
    },
  },
  listAlarmsDialog: {
    title: {
      notes: 'Title of all alarms dialog',
      message: 'Alarm log',
    },
    forEquipment: {
      notes:
        'Label for chips showing selected equipment for which alarms are displayed',
      message: 'Alarms for equipment',
    },
  },
  model: {
    severity: {
      notes: 'Name of severity property on alarm',
      message: 'Level',
      values: {
        low: {
          notes: 'Name of alarm severity low',
          message: 'Low',
        },
        medium: {
          notes: 'Name of alarm severity medium',
          message: 'Medium',
        },
        high: {
          notes: 'Name of alarm severity high',
          message: 'High',
        },
        critical: {
          notes: 'Name of alarm severity critical',
          message: 'Critical',
        },
        info: {
          notes: 'Name of alarm severity info',
          message: 'Info',
        },
      },
    },
    triggered: {
      notes: 'Name of triggered date and time property on alarm',
      message: 'Timestamp',
    },
    closed: {
      notes: 'Date and time when alarm was closed',
      message: 'Closed',
    },
    duration: {
      notes: 'How long the alarm was open, from triggered to closed',
      message: 'Duration',
    },
    name: {
      notes: 'Name of the name property on alarm',
      message: 'Name',
    },
    description: {
      notes: 'Name of the description property on alarm',
      message: 'Description',
    },
    status: {
      notes: 'Name of the status property on alarm',
      message: 'State',
      values: {
        open: {
          notes: 'Name of alarm status OPEN',
          message: 'Active',
        },
        closed: {
          notes: 'Name of alarm status CLOSED',
          message: 'Inactive',
        },
        stateless: {
          notes: 'Name of alarm status STATELESS',
          message: 'Stateless',
        },
      },
    },
    actions: {
      notes: 'Name of actions property on alarm',
      message: 'Action',
    },
    consequences: {
      notes: 'Name of consequences property on alarm',
      message: 'Consequence',
    },
    breachValue: {
      notes: 'Value that caused the threshold breach',
      message: 'Value',
    },
    thresholdLower: {
      notes: 'Lower threshold configured for the alarm signal',
      message: 'Lower threshold',
    },
    thresholdUpper: {
      notes: 'Upper threshold configured for the alarm signal',
      message: 'Upper threshold',
    },
    equipment: {
      notes: 'Name of equipment property on alarm',
      message: 'Equipment',
    },
    count: {
      notes:
        'Number of occurrences of alarm. Used for example when grouping alarms by severity.',
      message: 'Amount',
    },
  },
  action: {
    showAll: {
      notes: 'Action to show all alarms',
      message: 'Show all',
    },
  },
  cluster: {
    moreAlarms: {
      notes:
        'Text showing that there are more alarms than can be displayed for the cluster marker',
      message: 'There are more alarms.',
    },
    useZoomIn: {
      notes:
        'Instruction that zooming in reveals the individual alarms instead of a cluster',
      message: 'Zoom in to reveal individual alarms.',
    },
    useAlarmLog: {
      notes:
        'Instruction that the alarm log can be used to view details about the alarms in cluster',
      message: 'Visit the alarm log to view details about the alarms.',
    },
  },
  signalInterval,
  notificationPreferences,
};
